import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

export interface ApprovalConfiguration {
  regulate: {
    widgets: boolean;
    images: boolean;
    video: boolean;
    pdf: boolean;
  };
}

const UNREGULATED: ApprovalConfiguration = {
  regulate: { widgets: false, images: false, video: false, pdf: false },
};

/**
 * Reads the 'approval.configuration' tenant parameter (Java V085) that decides which
 * media asset types are regulated by the compliance approval workflow. Regulated types
 * may only be selected in pickers when the asset carries the 'Approved' compliance label.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalConfigService {
  private readonly http = inject(HttpClient);

  static readonly COMPLIANCE_DEFINITION_NAME = 'Compliance';
  static readonly STATUS_APPROVED = 'Approved';

  /**
   * The status every regulated asset receives the moment it is created —
   * `ApproveScreeningService.onMediaAssetCreated` applies it, then AI screening or a
   * human reviewer moves it on. String must stay in sync with Java's
   * `ComplianceStatus.UNDER_REVIEW("Under review")`; it is matched by label NAME, since
   * the label uuids are per-tenant (seeded by migration V085).
   */
  static readonly STATUS_UNDER_REVIEW = 'Under review';

  private readonly configuration$: Observable<ApprovalConfiguration> = this.http
    .get<ApprovalConfiguration>('/api/core/parameter/approval-configuration')
    .pipe(
      map((config) => ({ ...UNREGULATED, ...config, regulate: { ...UNREGULATED.regulate, ...config?.regulate } })),
      catchError(() => of(UNREGULATED)),
      shareReplay(1)
    );

  readonly configuration = toSignal(this.configuration$, { initialValue: UNREGULATED });

  /** Accepts media library types incl. the frontend pseudo-type 'personal_video'. */
  isRegulated(type: string | null | undefined): boolean {
    if (!type) {
      return false;
    }
    const regulate = this.configuration().regulate;
    switch (type) {
      case 'widget':
        return regulate.widgets;
      case 'image':
      case 'gif':
        return regulate.images;
      case 'video':
      case 'personal_video':
        return regulate.video;
      case 'document':
      case 'pdf':
        return regulate.pdf;
      default:
        return false;
    }
  }
}
